import type {
  SectorDraftInput,
  SectorSlots,
} from "@/domains/freelance/taxonomy/selector/types";
import type {
  OnboardingStore,
  SectorErrorCode,
  SectorValue,
} from "../context/OnboardingContext";

type SectorSection = Pick<OnboardingStore["sector"], "value" | "source">;

/**
 * The only thing the step asks for (AC16, OQ10): a primary sector. Zero
 * subcategories is fine, and so is no secondary.
 */
export const sectorStepError = (value: SectorValue): "required" | null =>
  value.job_category ? null : "required";

/**
 * The error code after a change. `sector_reset` (the sanitizer emptied a
 * sector that is no longer in the tree, AC20) stays until the freelancer picks
 * a primary: a change that still has none must not turn it into `required`.
 */
export const nextSectorError = (
  value: SectorValue,
  current: SectorErrorCode | null,
): SectorErrorCode | null => {
  const error = sectorStepError(value);
  return error && current === "sector_reset" ? "sector_reset" : error;
};

/**
 * The store section as the draft's input. Onboarding has no Jev proposal
 * (`/me` carries no `suggested_job_category` yet, PLAN §4.7), so `proposal` is
 * `null`; the CV / LinkedIn suggestions are already in the store with origin
 * `suggested`. `manual` and `confirmed` both read as confirmed.
 */
export const sectorDraftInputFromStore = (
  sector: SectorSection,
): SectorDraftInput => ({
  saved: {
    job_category: sector.value.job_category,
    job_category_secondary: sector.value.job_category_secondary,
    job_subcategories: sector.value.job_subcategories.map((sub) => ({
      key: sub.key,
      status: sub.origin === "suggested" ? "suggested" : "confirmed",
    })),
  },
  proposal: null,
  source: sector.source,
  container: "onboarding",
});

/** The draft's slots as the store value, each subcategory with its origin. */
export const sectorValueFromSlots = (
  slots: Readonly<SectorSlots>,
): SectorValue => ({
  job_category: slots[0]?.category ?? null,
  job_category_secondary: slots[1]?.category ?? null,
  job_subcategories: slots.flatMap((slot) =>
    (slot?.subs ?? []).map((sub) => ({ key: sub.key, origin: sub.origin })),
  ),
});

/**
 * Subcategories still carrying the CV / LinkedIn suggestion: what «N ambiti dal
 * CV» in the bar counts (AC9). Ones the freelancer added by hand are not.
 */
export const suggestedSubcategoryCount = (value: SectorValue): number =>
  value.job_subcategories.filter((sub) => sub.origin === "suggested").length;
